import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { CircleMarker, FeatureGroup, GeoJSON, MapContainer, Marker, TileLayer, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './GeoJSONMap.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

const DEFAULT_AUSTRALIA_BOUNDS = [
    [-45, 108],
    [-8, 155]
];

const DEFAULT_CENTER = [-25.2744, 133.7751];

const TILE_ENVELOPE = [
    [-50, 96],
    [6, 185]
];

const INTERACTION_PAN = 'pan';
const INTERACTION_POINT = 'point';
const MIN_BOUNDS_DRAG_PIXELS = 6;
const MAX_SELECTION_LABELS = 24;
const LABEL_COLLISION_PADDING = 6;
const MAX_LABEL_TEXT_LENGTH = 26;

const LABEL_ANCHOR_ICON = L.divIcon({
    className: 'selected-point-label-anchor',
    html: '',
    iconSize: [1, 1],
    iconAnchor: [0, 0]
});

const DATASET_ATTRIBUTION = 'Credits for this work are: Ryan, Lyndall; Debenham, Jennifer; Pascoe, Bill; '
    + 'Smith, Robyn; Owen, Chris; Richards, Jonathan; Gilbert, Stephanie; Anders, Robert J; Usher, Kaine; '
    + 'Price, Daniel; Newley, Jack; Brown, Mark; Le, Le Hoang; Fairbairn, Hedy Colonial Frontier Massacres in '
    + 'Australia 1788-1930 Newcastle: University of Newcastle, 2017-2022. Previous research was conducted by '
    + 'Freja Carmichael, Indy Medeiros, Angus Hooper, Greg Hooper. Current iteration credit to: Nina Cehak, '
    + 'Jonathan Richards, Judy Watson.';

const CustomPopupContent = ({ feature }) => (
    <div>
        <h4>{feature.properties.place}, {feature.properties.pastoral_district}</h4>
        <div className="scrollable-narrative">
            <p>{feature.properties.narrative}</p>
        </div>
        <p><i>{feature.properties.date_text}</i></p>
        <p>Language Group: {feature.properties.language_group || 'Not recorded'}</p>
    </div>
);

const toBounds = (bounds) => {
    if (!bounds) {
        return null;
    }
    return Array.isArray(bounds) ? L.latLngBounds(bounds) : bounds;
};

const AutoFitBounds = ({ bounds, maxZoom }) => {
    const map = useMap();
    const normalizedBounds = useMemo(() => toBounds(bounds), [bounds]);

    useEffect(() => {
        if (!normalizedBounds || !normalizedBounds.isValid()) {
            return;
        }
        map.fitBounds(normalizedBounds, { padding: [24, 24], maxZoom, animate: false });
    }, [map, normalizedBounds, maxZoom]);

    useEffect(() => {
        if (!normalizedBounds || !normalizedBounds.isValid()) {
            return undefined;
        }

        const handleResize = () => {
            map.invalidateSize({ pan: false });
            map.fitBounds(normalizedBounds, { padding: [24, 24], maxZoom, animate: false });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [map, normalizedBounds, maxZoom]);

    return null;
};

const MapClickHandler = ({ enabled, onCoordinateSelect }) => {
    const map = useMap();

    useEffect(() => {
        if (!enabled || !onCoordinateSelect) {
            return undefined;
        }

        const handleClick = (event) => {
            const { lat, lng } = event.latlng;
            onCoordinateSelect({
                latitude: Number(lat.toFixed(6)),
                longitude: Number(lng.toFixed(6))
            });
        };

        map.on('click', handleClick);
        return () => map.off('click', handleClick);
    }, [map, enabled, onCoordinateSelect]);

    return null;
};

const ImageOverlay = ({ imageUrl, bounds, opacity = 1 }) => {
    const map = useMap();

    useEffect(() => {
        const image = L.imageOverlay(imageUrl, bounds, { opacity }).addTo(map);
        return () => {
            map.removeLayer(image);
        };
    }, [map, imageUrl, bounds, opacity]);

    return null;
};

const BoundsDragSelector = ({ enabled, featureGroupRef, onBoundsChange }) => {
    const map = useMap();
    const drawingStateRef = useRef({
        drawing: false,
        startLatLng: null,
        startPoint: null,
        didDrag: false,
        layer: null
    });

    useEffect(() => {
        if (!map || !featureGroupRef.current || !enabled) {
            if (map && map.dragging && !map.dragging.enabled()) {
                map.dragging.enable();
            }
            return undefined;
        }

        if (map.dragging && map.dragging.enabled()) {
            map.dragging.disable();
        }

        const rectangleStyle = {
            color: '#2d6cdf',
            weight: 2,
            dashArray: '6',
            fill: true,
            fillColor: '#2d6cdf',
            fillOpacity: 0.16
        };

        const handleMouseDown = (event) => {
            if (event.originalEvent && event.originalEvent.button !== 0) {
                return;
            }
            const drawingState = drawingStateRef.current;
            drawingState.drawing = true;
            drawingState.startLatLng = event.latlng;
            drawingState.startPoint = event.containerPoint;
            drawingState.didDrag = false;
        };

        const handleMouseMove = (event) => {
            const drawingState = drawingStateRef.current;
            if (!drawingState.drawing || !drawingState.startLatLng || !drawingState.startPoint) {
                return;
            }

            const distance = drawingState.startPoint.distanceTo(event.containerPoint);
            if (distance < MIN_BOUNDS_DRAG_PIXELS) {
                return;
            }

            if (!drawingState.didDrag) {
                drawingState.didDrag = true;
                if (drawingState.layer) {
                    featureGroupRef.current.removeLayer(drawingState.layer);
                }
                featureGroupRef.current.clearLayers();
                drawingState.layer = L.rectangle(
                    L.latLngBounds(drawingState.startLatLng, event.latlng),
                    rectangleStyle
                );
                featureGroupRef.current.addLayer(drawingState.layer);
                return;
            }

            if (drawingState.layer) {
                drawingState.layer.setBounds(L.latLngBounds(drawingState.startLatLng, event.latlng));
            }
        };

        const finishDrawing = () => {
            const drawingState = drawingStateRef.current;
            if (!drawingState.drawing) {
                return;
            }
            drawingState.drawing = false;
            if (drawingState.didDrag && drawingState.layer) {
                const bounds = drawingState.layer.getBounds();
                onBoundsChange([[bounds.getSouth(), bounds.getWest()], [bounds.getNorth(), bounds.getEast()]]);
            }
            drawingState.startLatLng = null;
            drawingState.startPoint = null;
            drawingState.didDrag = false;
        };

        map.on('mousedown', handleMouseDown);
        map.on('mousemove', handleMouseMove);
        map.on('mouseup', finishDrawing);
        window.addEventListener('mouseup', finishDrawing);

        return () => {
            map.off('mousedown', handleMouseDown);
            map.off('mousemove', handleMouseMove);
            map.off('mouseup', finishDrawing);
            window.removeEventListener('mouseup', finishDrawing);
            drawingStateRef.current = {
                drawing: false,
                startLatLng: null,
                startPoint: null,
                didDrag: false,
                layer: null
            };
            if (map.dragging && !map.dragging.enabled()) {
                map.dragging.enable();
            }
        };
    }, [map, enabled, featureGroupRef, onBoundsChange]);

    return null;
};

const SelectedBoundsLabels = ({ geoJSONData, selectedBounds }) => {
    const map = useMap();
    const [labels, setLabels] = useState([]);

    const updateLabels = useCallback(() => {
        if (!selectedBounds || !geoJSONData || !Array.isArray(geoJSONData.features)) {
            setLabels([]);
            return;
        }

        const selectionBounds = toBounds(selectedBounds);
        if (!selectionBounds || !selectionBounds.isValid()) {
            setLabels([]);
            return;
        }

        const intersects = (a, b) => !(
            a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom
        );

        const mapSize = map.getSize();
        const accepted = [];
        const occupiedBoxes = [];
        const candidates = [];

        geoJSONData.features.forEach((feature, index) => {
            if (feature?.geometry?.type !== 'Point') {
                return;
            }
            const coordinates = feature.geometry.coordinates || [];
            const lng = Number(coordinates[0]);
            const lat = Number(coordinates[1]);

            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                return;
            }
            if (!selectionBounds.contains([lat, lng])) {
                return;
            }

            const place = String(feature?.properties?.place || '').trim();
            const eventTitle = String(feature?.properties?.event || '').trim();
            const labelBase = place || eventTitle || `${lat.toFixed(2)}, ${lng.toFixed(2)}`;
            const label = labelBase.length > MAX_LABEL_TEXT_LENGTH
                ? `${labelBase.slice(0, MAX_LABEL_TEXT_LENGTH - 1)}…`
                : labelBase;
            const point = map.latLngToContainerPoint([lat, lng]);

            candidates.push({
                id: String(feature?.properties?.global_id || `${lat}-${lng}-${index}`),
                lat,
                lng,
                label,
                point
            });
        });

        candidates.sort((a, b) => {
            if (a.point.y !== b.point.y) {
                return a.point.y - b.point.y;
            }
            return a.point.x - b.point.x;
        });

        for (const item of candidates) {
            const width = Math.max(74, Math.min(210, item.label.length * 7 + 18));
            const height = 24;
            const bottom = item.point.y - 10;
            const top = bottom - height;
            const left = item.point.x - width / 2;
            const right = item.point.x + width / 2;
            const paddedBox = {
                left: left - LABEL_COLLISION_PADDING,
                right: right + LABEL_COLLISION_PADDING,
                top: top - LABEL_COLLISION_PADDING,
                bottom: bottom + LABEL_COLLISION_PADDING
            };

            if (paddedBox.left < 0 || paddedBox.right > mapSize.x || paddedBox.top < 0 || paddedBox.bottom > mapSize.y) {
                continue;
            }
            if (occupiedBoxes.some((box) => intersects(paddedBox, box))) {
                continue;
            }

            occupiedBoxes.push(paddedBox);
            accepted.push(item);
            if (accepted.length >= MAX_SELECTION_LABELS) {
                break;
            }
        }

        setLabels(accepted);
    }, [geoJSONData, selectedBounds, map]);

    useEffect(() => {
        updateLabels();
    }, [updateLabels]);

    useEffect(() => {
        if (!selectedBounds) {
            setLabels([]);
            return undefined;
        }

        map.on('moveend', updateLabels);
        map.on('zoomend', updateLabels);
        map.on('resize', updateLabels);

        return () => {
            map.off('moveend', updateLabels);
            map.off('zoomend', updateLabels);
            map.off('resize', updateLabels);
        };
    }, [map, selectedBounds, updateLabels]);

    if (!selectedBounds || labels.length === 0) {
        return null;
    }

    return (
        <>
            {labels.map((item) => (
                <Marker
                    key={`label-${item.id}`}
                    position={[item.lat, item.lng]}
                    icon={LABEL_ANCHOR_ICON}
                    interactive={false}
                    keyboard={false}
                >
                    <Tooltip
                        permanent
                        direction="top"
                        offset={[0, -10]}
                        className="selected-point-tooltip"
                        opacity={0.97}
                    >
                        {item.label}
                    </Tooltip>
                </Marker>
            ))}
        </>
    );
};

const useResponsiveMapSettings = () => {
    const computeSettings = () => {
        if (typeof window === 'undefined') {
            return {
                initialZoom: 4,
                minZoom: 2.5,
                maxZoom: 7,
                maxNativeZoom: 6,
                detectRetina: false,
                mapHeight: '88vh'
            };
        }

        const width = window.innerWidth;
        const height = window.innerHeight || 900;
        const detectRetina = (window.devicePixelRatio || 1) > 1.2;
        const dynamicHeight = `${Math.max(70, Math.min(92, ((height - 110) / height) * 100)).toFixed(0)}vh`;

        if (width < 640) {
            return {
                initialZoom: 3.2,
                minZoom: 2.5,
                maxZoom: 6,
                maxNativeZoom: 5,
                detectRetina,
                mapHeight: '72vh'
            };
        }

        if (width < 1200) {
            return {
                initialZoom: 3.9,
                minZoom: 2.8,
                maxZoom: detectRetina ? 6.8 : 6.2,
                maxNativeZoom: 6,
                detectRetina,
                mapHeight: dynamicHeight
            };
        }

        return {
            initialZoom: 4.6,
            minZoom: 3,
            maxZoom: detectRetina ? 7.2 : 6.6,
            maxNativeZoom: 6,
            detectRetina,
            mapHeight: dynamicHeight
        };
    };

    const [settings, setSettings] = useState(computeSettings);

    useEffect(() => {
        const onResize = () => setSettings(computeSettings());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    return settings;
};

const GeoJSONMap = ({ geoJSONData, onCoordinateSelect }) => {
    const mapInstanceRef = useRef(null);
    const featureGroupRef = useRef(null);
    const responsiveSettings = useResponsiveMapSettings();
    const mapBounds = useMemo(() => L.latLngBounds(TILE_ENVELOPE), []);

    const [datasetBounds, setDatasetBounds] = useState(null);
    const [selectedBounds, setSelectedBounds] = useState(null);
    const [selectedPoint, setSelectedPoint] = useState(null);
    const [interactionMode, setInteractionMode] = useState(INTERACTION_PAN);
    const [isBoundsSelectionEnabled, setIsBoundsSelectionEnabled] = useState(false);
    const [tileUrl, setTileUrl] = useState('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png');
    const [overlays, setOverlays] = useState([]);

    const initialFitBounds = datasetBounds || DEFAULT_AUSTRALIA_BOUNDS;
    const cursorClass = isBoundsSelectionEnabled
        ? 'cursor-bounds'
        : (interactionMode === INTERACTION_POINT ? 'cursor-point' : 'cursor-pan');

    const defaultStyle = useMemo(() => ({
        color: '#333333',
        fillColor: '#555555',
        weight: 2,
        fillOpacity: 0.8,
        radius: 7
    }), []);

    const highlightStyle = useMemo(() => ({
        color: '#ff5733',
        fillColor: '#ff5733',
        weight: 3,
        fillOpacity: 0.6,
        radius: 7
    }), []);

    const onEachFeature = useCallback((feature, layer) => {
        if (feature.geometry.type !== 'Point') {
            const customPopup = ReactDOMServer.renderToString(<CustomPopupContent feature={feature} />);
            layer.bindPopup(customPopup);
        }

        layer.on('popupopen', () => layer.setStyle(highlightStyle));
        layer.on('popupclose', () => layer.setStyle(defaultStyle));
    }, [highlightStyle, defaultStyle]);

    const pointToLayer = useCallback((feature, latlng) => {
        const customPopup = ReactDOMServer.renderToString(<CustomPopupContent feature={feature} />);
        return L.circleMarker(latlng, defaultStyle).bindPopup(customPopup);
    }, [defaultStyle]);

    useEffect(() => {
        let cancelled = false;
        const probeLocalTiles = async () => {
            try {
                const response = await fetch('/tiles/0/0/0.png', { method: 'HEAD' });
                if (!cancelled && response.ok) {
                    setTileUrl('/tiles/{z}/{x}/{y}.png');
                }
            } catch (_error) {
                // Keep OSM fallback.
            }
        };

        probeLocalTiles();
        return () => {
            cancelled = true;
        };
    }, []);

    // Published artwork overlays are managed in the backend admin (upload +
    // drag-to-position). Each is rendered below as a Leaflet imageOverlay.
    useEffect(() => {
        let cancelled = false;
        const loadOverlays = async () => {
            try {
                const response = await fetch('/api/overlays/');
                if (!response.ok) {
                    return;
                }
                const data = await response.json();
                if (!cancelled && Array.isArray(data.overlays)) {
                    setOverlays(data.overlays);
                }
            } catch (_error) {
                // No overlays is a valid state; leave the map unobstructed.
            }
        };

        loadOverlays();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!geoJSONData) {
            setDatasetBounds(null);
            return;
        }
        try {
            const layer = L.geoJSON(geoJSONData);
            const bounds = layer.getBounds();
            if (bounds && bounds.isValid()) {
                setDatasetBounds([[bounds.getSouth(), bounds.getWest()], [bounds.getNorth(), bounds.getEast()]]);
            } else {
                setDatasetBounds(null);
            }
        } catch (_error) {
            setDatasetBounds(null);
        }
    }, [geoJSONData]);

    useEffect(() => {
        if (!mapInstanceRef.current) {
            return;
        }
        mapInstanceRef.current.invalidateSize({ pan: false });
    }, [responsiveSettings.mapHeight]);

    const handlePointSelection = useCallback((point) => {
        setSelectedPoint(point);
        if (onCoordinateSelect) {
            onCoordinateSelect(point);
        }
    }, [onCoordinateSelect]);

    const handleDrawBoundsChange = useCallback((bounds) => {
        setSelectedBounds(bounds);
    }, []);

    const clearSelectedBounds = useCallback(() => {
        setSelectedBounds(null);
        if (featureGroupRef.current) {
            featureGroupRef.current.clearLayers();
        }
    }, []);

    const focusSelectedBounds = useCallback(() => {
        if (!mapInstanceRef.current || !selectedBounds) {
            return;
        }
        const bounds = toBounds(selectedBounds);
        if (!bounds || !bounds.isValid()) {
            return;
        }
        mapInstanceRef.current.fitBounds(bounds, {
            padding: [18, 18],
            maxZoom: responsiveSettings.maxZoom
        });
    }, [selectedBounds, responsiveSettings.maxZoom]);

    return (
        <div className={`names-map-shell ${cursorClass}`} style={{ height: responsiveSettings.mapHeight }}>
            <MapContainer
                className="names-map-container"
                center={DEFAULT_CENTER}
                zoom={responsiveSettings.initialZoom}
                minZoom={responsiveSettings.minZoom}
                maxZoom={responsiveSettings.maxZoom}
                maxBounds={mapBounds}
                maxBoundsViscosity={1.0}
                preferCanvas
                style={{ height: '100%', width: '100%' }}
                zoomSnap={0.25}
                zoomDelta={0.5}
                scrollWheelZoom
                whenReady={(event) => {
                    mapInstanceRef.current = event.target;
                    event.target.invalidateSize({ pan: false });
                }}
            >
                <AutoFitBounds bounds={initialFitBounds} maxZoom={responsiveSettings.maxZoom} />
                <MapClickHandler
                    enabled={!isBoundsSelectionEnabled && interactionMode === INTERACTION_POINT}
                    onCoordinateSelect={handlePointSelection}
                />

                <TileLayer
                    url={tileUrl}
                    bounds={mapBounds}
                    noWrap
                    updateWhenZooming
                    updateWhenIdle={false}
                    keepBuffer={4}
                    maxNativeZoom={responsiveSettings.maxNativeZoom}
                    detectRetina={responsiveSettings.detectRetina}
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />

                {geoJSONData ? (
                    <GeoJSON
                        data={geoJSONData}
                        onEachFeature={onEachFeature}
                        style={defaultStyle}
                        pointToLayer={pointToLayer}
                        attribution={DATASET_ATTRIBUTION}
                    />
                ) : null}

                {selectedPoint ? (
                    <CircleMarker
                        center={[selectedPoint.latitude, selectedPoint.longitude]}
                        radius={8}
                        pathOptions={{ color: '#1f6de2', fillColor: '#1f6de2', fillOpacity: 0.75, weight: 2 }}
                    />
                ) : null}

                <FeatureGroup ref={featureGroupRef} />
                <BoundsDragSelector
                    enabled={isBoundsSelectionEnabled}
                    featureGroupRef={featureGroupRef}
                    onBoundsChange={handleDrawBoundsChange}
                />
                <SelectedBoundsLabels
                    geoJSONData={geoJSONData}
                    selectedBounds={selectedBounds}
                />

                {overlays.map((overlay) => (
                    <ImageOverlay
                        key={overlay.id}
                        imageUrl={overlay.imageUrl}
                        bounds={overlay.bounds}
                        opacity={overlay.opacity}
                    />
                ))}
            </MapContainer>

            <div className="map-interaction-toolbar" aria-label="Map interaction tools">
                <div className="map-toggle-row" role="group" aria-label="Interaction mode">
                    <button
                        type="button"
                        className={interactionMode === INTERACTION_PAN ? 'active' : ''}
                        onClick={() => setInteractionMode(INTERACTION_PAN)}
                    >
                        Pan mode
                    </button>
                    <button
                        type="button"
                        className={interactionMode === INTERACTION_POINT ? 'active' : ''}
                        onClick={() => setInteractionMode(INTERACTION_POINT)}
                    >
                        Point select
                    </button>
                </div>
                <div className="map-toggle-row" role="group" aria-label="Bounds controls">
                    <button
                        type="button"
                        className={isBoundsSelectionEnabled ? 'active' : ''}
                        onClick={() => setIsBoundsSelectionEnabled((enabled) => !enabled)}
                    >
                        {isBoundsSelectionEnabled ? 'Finish bounds' : 'Edit bounds'}
                    </button>
                    {selectedBounds ? (
                        <button type="button" onClick={clearSelectedBounds}>Clear bounds</button>
                    ) : null}
                    <button
                        type="button"
                        onClick={focusSelectedBounds}
                        disabled={!selectedBounds}
                    >
                        Focus bounds
                    </button>
                </div>
                <p className="map-toolbar-hint">
                    {isBoundsSelectionEnabled
                        ? 'Bounds editing is active. Click and drag on the map to draw a rectangle.'
                        : interactionMode === INTERACTION_POINT
                        ? 'Click the map to set a point for hearsay coordinates.'
                        : 'Pan and zoom freely. Switch to Point select when you need coordinates.'}
                </p>
            </div>
        </div>
    );
};

export default GeoJSONMap;
